import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  Animated,
  Easing,
  Platform,
  Pressable,
  Text,
  useWindowDimensions,
  View,
} from 'react-native'
import { LinearGradient } from 'expo-linear-gradient'
import { Apple, Smartphone } from 'lucide-react-native'
import QRCode from 'react-native-qrcode-svg'
import { useTheme } from '../../../src/theme/useTheme'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations'
import { LANDING_PHONE_MAX_WIDTH } from '../../../src/landing/constants/landingMarketingBreakpoints.constants'
import { APP_STORE_URL, GOOGLE_PLAY_URL } from '../../../constants/storeLinks'
import { openExternalUrl } from '../../../src/shared/helpers/openExternalUrl.helper'
import {
  getLandingGetTheAppStyles,
  landingGetTheAppGradientEnd,
  landingGetTheAppGradientStart,
} from './LandingGetTheApp.styles'

const STACKED_BREAKPOINT = 880
const QR_VALUE = GOOGLE_PLAY_URL
const QR_SIZE_DESKTOP = 104
const QR_SIZE_PHONE = 56

export function LandingGetTheApp() {
  const theme = useTheme()
  const styles = useMemo(() => getLandingGetTheAppStyles(theme), [theme])
  const { width } = useWindowDimensions()
  const isStacked = width > 0 && width < STACKED_BREAKPOINT
  const isPhone = width > 0 && width < LANDING_PHONE_MAX_WIDTH

  const blobAnim = useRef(new Animated.Value(0)).current
  const qrFloatAnim = useRef(new Animated.Value(0)).current

  const openGooglePlay = useCallback(() => openExternalUrl(GOOGLE_PLAY_URL), [])
  const openAppStore = useCallback(() => openExternalUrl(APP_STORE_URL), [])

  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(blobAnim, {
          toValue: 1,
          duration: 12000,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(blobAnim, {
          toValue: 0,
          duration: 12000,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    ).start()

    Animated.loop(
      Animated.sequence([
        Animated.timing(qrFloatAnim, {
          toValue: -6,
          duration: 2500,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(qrFloatAnim, {
          toValue: 0,
          duration: 2500,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    ).start()
  }, [blobAnim, qrFloatAnim])

  const blobTranslateX = blobAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, -60],
  })
  const blobTranslateY = blobAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 30],
  })
  const blobRotate = blobAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '15deg'],
  })

  const accentBlob = (
    <Animated.View
      pointerEvents="none"
      style={[
        styles.accentBlob,
        isPhone && styles.accentBlobPhone,
        {
          transform: [
            { translateX: blobTranslateX },
            { translateY: blobTranslateY },
            { rotate: blobRotate },
          ],
        },
      ]}
    />
  )

  const gradientFill = (
    <LinearGradient
      colors={[theme.primaryDark, theme.primary]}
      start={landingGetTheAppGradientStart}
      end={landingGetTheAppGradientEnd}
      style={styles.gradientFill}
    />
  )

  /* PHONE: completely separate vertical-stack layout — no flex sibling
     columns, no flex-grow children, just a single column of blocks. */
  if (isPhone) {
    return (
      <View
        style={[styles.section, styles.sectionPhone]}
        nativeID="get-the-app"
      >
        <View style={styles.container}>
          <View style={[styles.card, styles.cardPhone]}>
            {gradientFill}
            {accentBlob}

            <View style={styles.phoneStack}>
              <Text style={styles.phoneEyebrow}>Get the app</Text>
              <Text style={styles.phoneTitle}>
                One tap from a season of bragging rights.
              </Text>
              <Text style={styles.phoneBody}>
                Free forever. No gambling. iOS, Android & web.
              </Text>

              <View style={styles.phoneActions}>
                <Pressable
                  onPress={openAppStore}
                  accessibilityRole="link"
                  accessibilityLabel="Download on the App Store"
                  style={({ hovered, pressed }) => [
                    styles.phoneBtn,
                    styles.btnSecondary,
                    hovered && Platform.OS === 'web' && styles.btnSecondaryHover,
                    pressed && styles.btnSecondaryPressed,
                    Platform.OS === 'web' && WEB_HOVER_TRANSITION_STYLE,
                  ]}
                >
                  <Apple size={16} color={theme.white} />
                  <Text style={[styles.phoneBtnText, { color: theme.white }]}>
                    App Store
                  </Text>
                </Pressable>

                <Pressable
                  onPress={openGooglePlay}
                  accessibilityRole="link"
                  accessibilityLabel="Get it on Google Play"
                  style={({ hovered, pressed }) => [
                    styles.phoneBtn,
                    styles.btnSecondary,
                    hovered && Platform.OS === 'web' && styles.btnSecondaryHover,
                    pressed && styles.btnSecondaryPressed,
                    Platform.OS === 'web' && WEB_HOVER_TRANSITION_STYLE,
                  ]}
                >
                  <Smartphone size={16} color={theme.white} />
                  <Text style={[styles.phoneBtnText, { color: theme.white }]}>
                    Google Play
                  </Text>
                </Pressable>
              </View>

              <Animated.View
                style={[
                  styles.phoneQrRibbon,
                  { transform: [{ translateY: qrFloatAnim }] },
                ]}
              >
                <View style={styles.phoneQrFrame}>
                  <QRCode
                    value={QR_VALUE}
                    size={QR_SIZE_PHONE}
                    color={theme.primaryDark}
                    backgroundColor={theme.white}
                  />
                </View>
                <View style={styles.phoneQrTextWrap}>
                  <Text style={styles.phoneQrTitle}>Scan to get the app</Text>
                  <Text style={styles.phoneQrSubtitle}>
                    Get it on Google Play
                  </Text>
                  <View style={styles.phoneStorePill}>
                    <Text style={styles.phoneStorePillText}>
                      Now on iOS &amp; Android
                    </Text>
                  </View>
                </View>
              </Animated.View>
            </View>
          </View>
        </View>
      </View>
    )
  }

  /* TABLET (stacked) and DESKTOP — original two-block layout. */
  return (
    <View style={styles.section} nativeID="get-the-app">
      <View style={styles.container}>
        <View style={[styles.card, isStacked && styles.cardStacked]}>
          {gradientFill}
          {accentBlob}

          <View style={[styles.leftCol, isStacked && styles.leftColStacked]}>
            <Text style={styles.eyebrow}>Get the app</Text>
            <Text style={[styles.h2, isStacked && styles.h2Stacked]}>
              Your group is one tap away from a season of bragging rights.
            </Text>
            <Text style={styles.p}>
              Free forever. No gambling. No nonsense. Just the most fun you'll have
              arguing about football — on iOS, Android, and the web.
            </Text>

            <View style={styles.actions}>
              <Pressable
                onPress={openAppStore}
                accessibilityRole="link"
                accessibilityLabel="Download on the App Store"
                style={({ hovered, pressed }) => [
                  styles.btnBase,
                  styles.btnSecondary,
                  hovered && Platform.OS === 'web' && styles.btnSecondaryHover,
                  pressed && styles.btnSecondaryPressed,
                  Platform.OS === 'web' && WEB_HOVER_TRANSITION_STYLE,
                ]}
              >
                <Apple size={18} color={theme.white} />
                <Text style={styles.btnSecondaryText}>App Store</Text>
              </Pressable>

              <Pressable
                onPress={openGooglePlay}
                accessibilityRole="link"
                accessibilityLabel="Get it on Google Play"
                style={({ hovered, pressed }) => [
                  styles.btnBase,
                  styles.btnSecondary,
                  hovered && Platform.OS === 'web' && styles.btnSecondaryHover,
                  pressed && styles.btnSecondaryPressed,
                  Platform.OS === 'web' && WEB_HOVER_TRANSITION_STYLE,
                ]}
              >
                <Smartphone size={18} color={theme.white} />
                <Text style={styles.btnSecondaryText}>Google Play</Text>
              </Pressable>
            </View>
          </View>

          <View style={[styles.rightCol, isStacked && styles.rightColStacked]}>
            <Animated.View
              style={[styles.qrCard, { transform: [{ translateY: qrFloatAnim }] }]}
            >
              <View style={styles.qrFrame}>
                <QRCode
                  value={QR_VALUE}
                  size={QR_SIZE_DESKTOP}
                  color={theme.primaryDark}
                  backgroundColor={theme.white}
                />
              </View>
              <View style={styles.qrText}>
                <Text style={styles.qrTitle}>Scan to get the app</Text>
                <Text style={styles.qrSubtitle}>Get it on Google Play</Text>
                <View style={styles.storePill}>
                  <Text style={styles.storePillText}>Now on iOS &amp; Android</Text>
                </View>
              </View>
            </Animated.View>
          </View>
        </View>
      </View>
    </View>
  )
}
